import React, { useState, useEffect } from 'react';
import { logsAPI } from '../services/api';
import Card from '../components/Card';
import Input from '../components/Input';
import SearchInput from '../components/SearchInput';
import { TableSkeleton } from '../components/LoadingSpinner';
import { useToast } from '../hooks/useToast';
import { LogsIcon } from '../components/Icons';
import './Logs.css';

export default function Logs() {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadFilter, setLeadFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadFilter]);

  useEffect(() => {
    let filtered = logs;

    if (statusFilter) {
      filtered = filtered.filter(log => log.status === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(log =>
        log.lead_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.campaign_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.lead_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  }, [searchQuery, statusFilter, logs]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (leadFilter) filters.lead_id = leadFilter;
      
      const response = await logsAPI.getAll(filters);
      // Handle paginated response
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setLogs(data);
    } catch (error) {
      console.error('Failed to load logs:', error);
      if (error.response?.status === 403) {
        toast.warning('Authentication required. Please login.');
      } else {
        toast.error('Failed to load logs. Check console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Outreach Logs</h1>
        </div>
        <Card>
          <TableSkeleton rows={5} cols={6} />
        </Card>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Outreach Logs</h1>
          <p className="page-subtitle">View all outreach activity and delivery status</p>
        </div>
      </div>

      <Card>
        <div className="filters-section">
          <div className="filter-group">
            <Input
              label="Lead ID Filter"
              type="text"
              value={leadFilter}
              onChange={(e) => setLeadFilter(e.target.value)}
              placeholder="Filter by Lead ID"
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="SENT">Sent</option>
              <option value="DELIVERED">Delivered</option>
              <option value="FAILED">Failed</option>
              <option value="BOUNCED">Bounced</option>
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1, maxWidth: '400px' }}>
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by lead, campaign..."
            />
          </div>
        </div>
      </Card>

      {filteredLogs.length === 0 ? (
        <Card>
          <div className="empty-state">
            <div className="empty-state-icon">
              <LogsIcon size={48} style={{ opacity: 0.5 }} />
            </div>
            <div className="empty-state-title">
              {searchQuery || statusFilter || leadFilter ? 'No logs found' : 'No logs yet'}
            </div>
            <div className="empty-state-description">
              {searchQuery || statusFilter || leadFilter
                ? 'Try adjusting your filters'
                : 'Outreach logs will appear here once campaigns start sending messages'}
            </div>
          </div>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Lead</th>
                  <th>Campaign</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Reply</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={log.id}>
                    <td>
                      <span className="text-secondary">
                        {log.sent_at ? new Date(log.sent_at).toLocaleString() : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div className="log-lead-name">{log.lead_name || 'N/A'}</div>
                        <div className="text-secondary text-sm">{log.lead_email || 'N/A'}</div>
                      </div>
                    </td>
                    <td>
                      <span className="text-secondary">{log.campaign_name || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="badge badge-channel">{log.channel || 'N/A'}</span>
                    </td>
                    <td>
                      <span className={`status-badge status-${log.status?.toLowerCase()}`}>
                        {log.status || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {log.reply_received ? (
                        <span className="reply-indicator reply-yes">Yes</span>
                      ) : (
                        <span className="reply-indicator reply-no">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
